import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, Package, Truck, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, isAdmin, isDriver, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="glass-nav sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src="/logo-cropped.png" alt="NarkOnline" className="h-9 w-9 object-contain" />
            <span className="text-xl font-bold text-green-900 hidden sm:block">NarkOnline</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/products" className="text-stone-600 hover:text-green-900 font-medium transition-colors" data-testid="nav-products">
              Products
            </Link>
            <Link to="/how-it-works" className="text-stone-600 hover:text-green-900 font-medium transition-colors" data-testid="nav-how-it-works">
              How It Works
            </Link>
            
            {isAuthenticated && !isAdmin && !isDriver && (
              <Link to="/orders" className="text-stone-600 hover:text-green-900 font-medium transition-colors" data-testid="nav-orders">
                My Orders
              </Link>
            )}
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-3">
            {/* Cart - visible for customers and guests */}
            {!isAdmin && !isDriver && (
              <Link to="/cart" className="relative p-2 hover:bg-stone-100 rounded-full transition-colors" data-testid="nav-cart">
                <ShoppingCart className="w-6 h-6 text-stone-700" />
                {cart.item_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-600 text-white text-xs rounded-full flex items-center justify-center font-semibold" data-testid="cart-count">
                    {cart.item_count}
                  </span>
                )}
              </Link>
            )}

            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    <div className="w-8 h-8 rounded-full bg-green-900 flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-stone-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  
                  {isDriver && (
                    <DropdownMenuItem onClick={() => navigate('/driver')} data-testid="menu-driver">
                      <Truck className="w-4 h-4 mr-2" />
                      Driver Dashboard
                    </DropdownMenuItem>
                  )}
                  
                  {!isAdmin && !isDriver && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/orders')} data-testid="menu-orders">
                        <Package className="w-4 h-4 mr-2" />
                        My Orders
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/driver/signup')} data-testid="menu-become-driver">
                        <Truck className="w-4 h-4 mr-2" />
                        Become a Driver
                      </DropdownMenuItem>
                    </>
                  )}
                  
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth">
                <Button className="btn-primary" data-testid="nav-login">
                  Sign In
                </Button>
              </Link>
            )}

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 hover:bg-stone-100 rounded-full"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-stone-200 bg-white">
          <div className="px-4 py-4 space-y-3">
            <Link 
              to="/products" 
              className="block py-2 text-stone-700 font-medium"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-products"
            >
              Products
            </Link>
            {isAuthenticated && !isAdmin && !isDriver && (
              <Link 
                to="/orders" 
                className="block py-2 text-stone-700 font-medium"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-orders"
              >
                My Orders
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
